import { create } from 'zustand';
import type { SimulationSession } from '../../sim-onboarding/types';
import { markSimCockpit, SS } from '../../sim-onboarding';

/**
 * Sessão municipal em foco no cockpit (painel SSE ↔ telemetria).
 * Diferente de ac_sessions (JWTs por ator): aqui é SimulationSession do backend.
 */
interface SimSessionFocusState {
  focusedSessionId: string | null;
  focusedSession: SimulationSession | null;
  setFocusedSession: (session: SimulationSession | null) => void;
  /** Mantém foco coerente com a lista SSE/REST (auto-seleciona se necessário). */
  syncFromSessions: (sessions: SimulationSession[]) => void;
}

function storedSessionId(): string | null {
  return SS.get('sim_session_id') || localStorage.getItem('ac_sim_session_id');
}

export const useSimSessionFocus = create<SimSessionFocusState>((set, get) => ({
  focusedSessionId: storedSessionId(),
  focusedSession: null,

  setFocusedSession: (session) => {
    if (!session) {
      set({ focusedSessionId: null, focusedSession: null });
      return;
    }
    markSimCockpit(session.id);
    set({ focusedSessionId: session.id, focusedSession: session });
  },

  syncFromSessions: (sessions) => {
    if (!sessions?.length) {
      set({ focusedSession: null });
      return;
    }

    const { focusedSessionId, setFocusedSession } = get();
    const byId = new Map(sessions.map((s) => [s.id, s]));

    if (focusedSessionId && byId.has(focusedSessionId)) {
      set({ focusedSession: byId.get(focusedSessionId)! });
      return;
    }

    const stored = storedSessionId();
    if (stored && byId.has(stored)) {
      setFocusedSession(byId.get(stored)!);
      return;
    }

    const running = sessions.filter((s) => s.status === 'RUNNING');
    if (running.length === 1) {
      setFocusedSession(running[0]);
      return;
    }

    const active = sessions.filter((s) =>
      s.status === 'RUNNING' || s.status === 'PAUSED' || s.status === 'PREPARING',
    );
    if (active.length === 1) {
      setFocusedSession(active[0]);
      return;
    }

    // Múltiplas sem foco: não escolhe sozinho — UI pede seleção
    set({ focusedSession: null });
  },
}));
